// =========================
// InfoTooltip — shared "ⓘ" icon with hover/focus popup.
//
// Extracted from TrendCard.js so DealAnalyzer + MyAnalyses (DEF-016) can
// reuse the same affordance for metric explanations. Behavior is identical
// to the TrendCard implementation with one exception: whiteSpace changed
// from "nowrap" to "normal" so multi-sentence explanations wrap inside the
// 260px popup width instead of overflowing the viewport.
//
// Styling note: still uses raw hex colors. TrendCard (the original home)
// is pre-v1.1.0 light-themed and slated for tokenization during the
// v1.2.0 child-component dark-theme pass — that pass will tokenize the
// popup at the same time.
// =========================

import React, { useState } from "react";

const COLORS = {
  subtle: "#6b7280",
};

export function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      aria-label="More info"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: "help",
        outline: "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: `1px solid ${COLORS.subtle}`,
          color: COLORS.subtle,
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          fontStyle: "italic",
          textTransform: "none",
          letterSpacing: 0,
        }}
      >
        i
      </span>
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            width: 260,
            background: "#FEFCE8",
            color: "#1C1917",
            border: "1px solid #854D0E",
            padding: "8px 10px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: 0,
            textTransform: "none",
            whiteSpace: "normal",
            textAlign: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            pointerEvents: "none",
          }}
        >
          {text}
          {/* Caret: two stacked downward-pointing triangles, anchored to the */}
          {/* bottom edge, horizontally centered over the icon below.         */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "7px solid transparent",
              borderRight: "7px solid transparent",
              borderTop: "7px solid #854D0E",
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "calc(100% - 1px)",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #FEFCE8",
            }}
          />
        </span>
      )}
    </span>
  );
}

// =========================
// METRIC_TOOLTIPS — DEF-016
// Plain-language explanations for the four headline metrics shown on the
// DealAnalyzer answer panel and the MyAnalyses card grid. Both surfaces
// import from here so any future copy edits stay in one place.
// =========================
export const METRIC_TOOLTIPS = {
  dscr:
    "DSCR (Debt Service Coverage Ratio) — How much more the property earns " +
    "than the loan costs. 1.5 means $1.50 earned for every $1.00 of payment. " +
    "Lenders usually want 1.25+.",
  noi:
    "NOI (Net Operating Income) — What's left each month after rent, " +
    "expenses, taxes, and insurance, but before the loan payment. " +
    "Higher is better.",
  projectedMonthlyRevenue:
    "What we estimate the lot can earn each month as a short-term rental, " +
    "based on nearby comparable listings.",
  dealScore:
    "An at-a-glance grade for the deal (A–F) based on cash flow, DSCR, the " +
    "market, and the price. A is excellent; F means walk away.",
};
